/**
 * @fileoverview Crawlable Vietnamese URLs (`/vi/...`) for the public marketing pages.
 *
 * Googlebot sends no Accept-Language or cookie, so locale-by-header pages only
 * ever show it English. These helpers give the pages that have Vietnamese
 * copy their own `/vi` URL. The middleware rewrites `/vi/<path>` to `/<path>`
 * and marks the request with {@link PATH_LOCALE_HEADER}; metadata and the
 * sitemap use the same list to emit canonical and hreflang links.
 *
 * @module lib/i18n/localized-paths
 */

const VI_URL_PREFIX = "/vi";

/** Request header the middleware sets on `/vi/...` rewrites so the server renders Vietnamese. */
export const PATH_LOCALE_HEADER = "x-aideco-path-locale";

/**
 * English paths that also exist at `/vi/<path>` and carry hreflang alternates.
 * Only pages rendered per request can show Vietnamese. The other marketing pages
 * (`dynamic = "force-static"`: feature, about, blog, ...) are built once in English,
 * so listing one here would publish an English page under a Vietnamese URL.
 */
export const VI_INDEXABLE_PATHS: readonly string[] = ["/", "/pricing"];

export function isViIndexablePath(path: string): boolean {
  return VI_INDEXABLE_PATHS.includes(path);
}

/** `/pricing` -> `/vi/pricing`, `/` -> `/vi`. */
export function toViPath(path: string): string {
  return path === "/" ? VI_URL_PREFIX : `${VI_URL_PREFIX}${path}`;
}

/** `/vi/pricing` -> `/pricing`, `/vi` -> `/`, anything not under `/vi` -> null. */
export function stripViPrefix(pathname: string): string | null {
  if (pathname === VI_URL_PREFIX) {
    return "/";
  }
  return pathname.startsWith(`${VI_URL_PREFIX}/`) ? pathname.slice(VI_URL_PREFIX.length) : null;
}

/**
 * Canonical plus hreflang alternates for an English path, or just the canonical
 * when the page has no `/vi` copy. `renderedLocale` is the language of the HTML
 * being produced, so each copy canonicalises to itself.
 */
export function buildLocaleAlternates(path: string, renderedLocale: "en" | "vi") {
  if (!isViIndexablePath(path)) {
    return { canonical: path };
  }
  return {
    canonical: renderedLocale === "vi" ? toViPath(path) : path,
    languages: {
      en: path,
      vi: toViPath(path),
      "x-default": path,
    },
  };
}
